import { _, Features, Enum, NullableString, MaskedDate, NullableMaskedDate, FeatureArray, ImageBlob, FingerprintsBlob, DocumentField, FeaturesI18N, IFormattingOptions } from 'controls';
import { UTCDate } from 'types';
import { Utils } from './utils';

export class Gender extends Enum {
  static readonly Male: Gender = new Gender('0');
  static readonly Female: Gender = new Gender('1');
}

export class InputMedia extends Enum {
  static readonly Manual: InputMedia = new InputMedia('0');
  static readonly Scan: InputMedia = new InputMedia('1');
  static readonly QrCodeSmall: InputMedia = new InputMedia('2');
  static readonly QrCode: InputMedia = new InputMedia('3');
  static readonly Xml: InputMedia = new InputMedia('4');
  static readonly Copy: InputMedia = new InputMedia('5');
}

export class InputMode extends Enum {
  static readonly KD: InputMode = new InputMode('0');
  static readonly PSF: InputMode = new InputMode('1');
  static readonly RkzuLCOD: InputMode = new InputMode('2');
  static readonly RkzuUT: InputMode = new InputMode('4');
  static readonly VO: InputMode = new InputMode('5');
  static readonly AP: InputMode = new InputMode('6');
  static readonly HSM: InputMode = new InputMode('7');
  static readonly UK: InputMode = new InputMode('8');
}

export class ForeignPassportType extends Enum {
  static readonly Common: ForeignPassportType = new ForeignPassportType('1');
  static readonly Diplomatic: ForeignPassportType = new ForeignPassportType('2');
  static readonly Service: ForeignPassportType = new ForeignPassportType('3');
}

export class AgeGroup extends Enum {
  static readonly Adult = new AgeGroup('0');
  static readonly Teenager = new AgeGroup('1');
  static readonly Kid = new AgeGroup('2');
}

export class RequestGoal extends Enum {
  static readonly LivingAbroad: RequestGoal = new RequestGoal('1');
  static readonly TemporaryTrips: RequestGoal = new RequestGoal('0');
}

export class RequestCause extends Enum {
  static readonly FirstTime: RequestCause = new RequestCause('0');
  static readonly SecondPassport: RequestCause = new RequestCause('5');
  static readonly InsteadOfUsedPassport: RequestCause = new RequestCause('1');
  static readonly InsteadOfSpoiltPassport: RequestCause = new RequestCause('2');
  static readonly InsteadOfLostPassport: RequestCause = new RequestCause('3');
  static readonly SecondPassportUntilFirstExpired: RequestCause = new RequestCause('4');
}

export class ApplicantIdentityDocumentType extends Enum {
  static readonly BirthCertificate: ApplicantIdentityDocumentType = new ApplicantIdentityDocumentType('1');
  static readonly RussianPassport: ApplicantIdentityDocumentType = new ApplicantIdentityDocumentType('3');
  static readonly NoDocument: ApplicantIdentityDocumentType = new ApplicantIdentityDocumentType('5');
}

export class RepresentativeIdentityDocumentType extends Enum {
  static readonly Custom: RepresentativeIdentityDocumentType = new RepresentativeIdentityDocumentType('0');
  static readonly RussianPassport: RepresentativeIdentityDocumentType = new RepresentativeIdentityDocumentType('3');
  static readonly RussianForeignPassport: RepresentativeIdentityDocumentType = new RepresentativeIdentityDocumentType('4');
}

export class AdultSpecialConditions extends Enum {
  static readonly None: AdultSpecialConditions = new AdultSpecialConditions('0');
  static readonly CitizenshipDecision: AdultSpecialConditions = new AdultSpecialConditions('1');
  static readonly PresidentEdict: AdultSpecialConditions = new AdultSpecialConditions('2');
  static readonly SoldierPermission: AdultSpecialConditions = new AdultSpecialConditions('3');
}

export class TeenagerSpecialConditions extends Enum {
  static readonly None: TeenagerSpecialConditions = new TeenagerSpecialConditions('0');
  static readonly CitizenshipDecision: TeenagerSpecialConditions = new TeenagerSpecialConditions('1');
  static readonly PresidentEdict: TeenagerSpecialConditions = new TeenagerSpecialConditions('2');
}

export class FingerprintsRequired extends Enum {
  static readonly NotRequired: FingerprintsRequired = new FingerprintsRequired('0');
  static readonly Required: FingerprintsRequired = new FingerprintsRequired('1');
}

export class RequestPriority extends Enum {
  static readonly None: RequestPriority = new RequestPriority('');
  static readonly Normal: RequestPriority = new RequestPriority('0');
  static readonly High: RequestPriority = new RequestPriority('1');
  static readonly VeryHigh: RequestPriority = new RequestPriority('2');
}

export class PersonalizedFPassportInfo {
  @_ number: string = '';
  @_ issueDate: MaskedDate = new MaskedDate();
  @_ expireDate: MaskedDate = new MaskedDate();
  @_ authorityRus: string = '';
  @_ machineReadableZoneLine1: string = '';
  @_ machineReadableZoneLine2: string = '';
  @_ activationKey: number[] = [];
}
Features.maskedDateOptions(PersonalizedFPassportInfo, x => x.issueDate, true, false, false, null);
Features.maskedDateOptions(PersonalizedFPassportInfo, x => x.expireDate, true, false, false, null);

export class FullName {
  @_ lastName: string = '';
  @_ firstName: string = '';
  @_ middleName: NullableString = new NullableString();

  asString(): string {
    return this.lastName + ' ' + this.firstName + ' ' + (this.middleName.value != null ? this.middleName.value : '');
  }

  asShortString(): string {
    return (!!this.lastName ? this.lastName + ' ' : '')
      + (!!this.firstName ? this.firstName.substr(0, 1) + '.' : '')
      + (this.middleName.value != null && this.middleName.value.length > 0 ? this.middleName.value.substr(0, 1) + '.' : '');
  }
}

export class PersonIdentity {
  @_ fullName: FullName = new FullName();
  @_ birthDate: MaskedDate = new MaskedDate();
}
Features.maskedDateOptions(PersonIdentity, x => x.birthDate, true, false, true, () => UTCDate.today(new Date()));

export class PersonAddress {
  @_ index: string = '';
  @_ country: string = '';
  @_ admDivision: NullableString = new NullableString();
  @_ settlement: string = '';
  @_ street: NullableString = new NullableString();
  @_ building: NullableString = new NullableString();
  @_ housing: NullableString = new NullableString();
  @_ structure: NullableString = new NullableString();
  @_ flat: NullableString = new NullableString();
  @_ phone: string = '';
}

export class RepresentativeAddress {
  @_ text: string = '';
}

export class ApplicantIdentityDocument {
  @_ type: ApplicantIdentityDocumentType = new ApplicantIdentityDocumentType();
  @_ series: string = '';
  @_ number: string = '';
  @_ issuer: string = '';
  @_ issueDate: MaskedDate = new MaskedDate();
}
Features.stringOptions(ApplicantIdentityDocument, x => x.issuer, true, false, false);
Features.maskedDateOptions(ApplicantIdentityDocument, x => x.issueDate, true, false, false, null);

export class RepresentativeIdentityDocument {
  @_ type: RepresentativeIdentityDocumentType = new RepresentativeIdentityDocumentType();
  @_ series: string = '';
  @_ number: string = '';
  @_ issuer: string = '';
  @_ issueDate: MaskedDate = new MaskedDate();
}
Features.stringOptions(RepresentativeIdentityDocument, (x) => x.issuer, true, false, false);
Features.maskedDateOptions(RepresentativeIdentityDocument, x => x.issueDate, true, false, false, null);

export class ActiveFPassportInfo {
  @_ series: string = '';
  @_ number: string = '';
  @_ issuer: string = '';
  @_ issueDate: MaskedDate = new MaskedDate();
  @_ mustBeCanceled: boolean = false;
  @_ next: boolean = false;
}
Features.maskedDateOptions(ActiveFPassportInfo, x => x.issueDate, true, false, false, null);

export class ActiveFPassportsInfo {
  @_ p1: ActiveFPassportInfo = new ActiveFPassportInfo();
  @_ p2: ActiveFPassportInfo = new ActiveFPassportInfo();
}

export class NameChange {
  @_ lastName: string = '';
  @_ firstName: string = '';
  @_ middleName: NullableString = new NullableString();
  @_ year: MaskedDate = new MaskedDate();
  @_ location: string = '';

  static compare(a: NameChange, b: NameChange): number {
    return a.year.value.localeCompare(b.year.value);
  }
}
Features.maskedDateOptions(NameChange, x => x.year, true, false, true, null);
Features.arrayAppereance(NameChange,
  (a, b) => NameChange.compare(a, b),
  undefined,
  new Map([
    [1250, [320, 320, 320]]
  ]),
  [
    { prop: x => x.lastName, viewFractionSize: 3, setters: [() => ({ caption: 'field.nameChanges.same', func: (record, features) => record.lastName = features.applicant.personIdentity.fullName.lastName })], compact: true },
    { prop: x => x.firstName, viewFractionSize: 3, setters: [() => ({ caption: 'field.nameChanges.same', func: (record, features) => record.firstName = features.applicant.personIdentity.fullName.firstName })], compact: true },
    { prop: x => x.middleName, viewFractionSize: 3, setters: [() => ({ caption: 'field.nameChanges.same', func: (record, features) => record.middleName.value = features.applicant.personIdentity.fullName.middleName.value })], compact: true },
    { prop: x => x.year, viewFractionSize: 1 },
    { prop: x => x.location, viewFractionSize: 3, minTableWidthVisible: 800 }
  ],
  {
    firstLineFormatter: (nc: NameChange) => [nc.lastName, nc.firstName, nc.middleName.value].join(' '),
    secondLineFormatter: (nc: NameChange) => nc.location + ' (' + Utils.maskedDateToDotsFormat(nc.year.value) + ')'
  },
  []
);

export class NameChangeForRep {
  @_ lastName: string = '';
  @_ firstName: string = '';
  @_ middleName: NullableString = new NullableString();
  @_ year: MaskedDate = new MaskedDate();
  @_ location: string = '';

  static compare(a: NameChange, b: NameChange): number {
    return a.year.value.localeCompare(b.year.value);
  }
}
Features.maskedDateOptions(NameChangeForRep, x => x.year, false, false, true, null);
Features.arrayAppereance(NameChangeForRep,
  (a, b) => NameChangeForRep.compare(a, b),
  undefined,
  new Map([
    [1220, [300, 300, 300]]
  ]),
  [
    { prop: x => x.lastName, viewFractionSize: 3, setters: [() => ({ caption: 'field.nameChanges.same', func: (record, features) => record.lastName = features.representative.personIdentity.fullName.lastName })] },
    { prop: x => x.firstName, viewFractionSize: 3, setters: [() => ({ caption: 'field.nameChanges.same', func: (record, features) => record.firstName = features.representative.personIdentity.fullName.firstName })] },
    { prop: x => x.middleName, viewFractionSize: 3, setters: [() => ({ caption: 'field.nameChanges.same', func: (record, features) => record.middleName.value = features.representative.personIdentity.fullName.middleName.value })] },
    { prop: x => x.year, viewFractionSize: 1 },
    { prop: x => x.location, viewFractionSize: 3, minTableWidthVisible: 800 }
  ],
  {
    firstLineFormatter: (nc: NameChangeForRep) => [nc.lastName, nc.firstName, nc.middleName.value].join(' '),
    secondLineFormatter: (nc: NameChangeForRep) => nc.location + ' (' + nc.year.value.substr(0, 4) + ')'
  },
  []
);

export class JobRecord {
  @_ position: string = '';
  @_ address: string = '';
  @_ from: MaskedDate = new MaskedDate();
  @_ to: NullableMaskedDate = new NullableMaskedDate();

  static compare(a: JobRecord, b: JobRecord): number {
    let result = (a.to.value || '9999').localeCompare(b.to.value || '9999');
    if (result) {
      return result;
    }
    result = a.from.value.localeCompare(b.from.value);
    if (result) {
      return result;
    }
    return 0;
  }

  static next(lastRecord: JobRecord, arr: JobRecord[]): JobRecord | null {
    if (!lastRecord || !lastRecord.to.value || arr.some(x => !x.to.value)) {
      return null;
    }
    const nextRecord = new JobRecord();
    nextRecord.from.value = lastRecord.to.value;
    return nextRecord;
  }
}
Features.stringOptions(JobRecord, x => x.position, true, false, false);
Features.stringOptions(JobRecord, x => x.address, true, false, false);
Features.maskedDateOptions(JobRecord, x => x.from, false, true, false, null);
Features.maskedDateOptions(JobRecord, x => x.to, false, true, false, null);
Features.arrayAppereance(JobRecord,
  (a, b) => JobRecord.compare(a, b),
  (last, arr) => JobRecord.next(last, arr),
  new Map([
    [1050, [380, 380]]
  ]),
  [
    {
      prop: x => x.position, viewFractionSize: 5, setters: [
        () => ({ caption: 'field.jobRecords.unemployed', func: (record, features) => { record.position = features.formatUnemployment(); } })
      ],
      compact: true
    },
    {
      prop: x => x.address, viewFractionSize: 5, setters: [
        () => ({ caption: 'field.jobRecords.copyPermAddress', func: (record, features) => { record.address = features.formatPermAddress(); } }),
        () => ({ caption: 'field.jobRecords.copyTempAddress', func: (record, features) => { record.address = features.formatTempAddress(); } })
      ]
    },
    { prop: x => x.from, viewFractionSize: 3 },
    { prop: x => x.to, viewFractionSize: 3 }
  ],
  {
    firstLineFormatter: (r: JobRecord, o: IFormattingOptions) => { return r.position },
    secondLineFormatter: (r: JobRecord, o: IFormattingOptions) => { return o.dateFormatter(r.from) + ' → ' + o.dateFormatter(r.to, 'MISS.ARRAY.JobRecord.to'); }
  },
  []
);

export class HRManager {
  @_ fullName: FullName = new FullName();
  @_ position: string = '';
  @_ phone: string = '';
}

export class CitizenshipCertificate {
  @_ active: boolean = false;
  @_ number: string = '';
  @_ date: MaskedDate = new MaskedDate();
  @_ officerPost: string = '';
  @_ officerName: string = '';
  @_ testimony: string = '';
}
Features.maskedDateOptions(CitizenshipCertificate, x => x.date, true, false, false, null);
Features.stringOptions(CitizenshipCertificate, x => x.testimony, true, true, false);

export class PresidentEdict {
  @_ number: string = '';
  @_ date: MaskedDate = new MaskedDate();
}
Features.maskedDateOptions(PresidentEdict, x => x.date, true, false, false, null);

export class CitizenshipDecision {
  @_ number: string = '';
  @_ date: MaskedDate = new MaskedDate();
  @_ officerPost: string = '';
  @_ officerName: string = '';
  @_ article: string = '';
}
Features.maskedDateOptions(CitizenshipDecision, x => x.date, true, false, false, null);

export class SoldierPermission {
  @_ number: string = '';
  @_ date: MaskedDate = new MaskedDate();
  @_ signerPost: string = '';
  @_ signerName: string = '';
  @_ agreementNumber: string = '';
  @_ agreementDate: MaskedDate = new MaskedDate();
  @_ deciderName: string = '';
  @_ militaryUnit: string = '';
}
Features.maskedDateOptions(SoldierPermission, x => x.date, true, false, false, null);
Features.maskedDateOptions(SoldierPermission, x => x.agreementDate, true, false, false, null);

export class DipServiceControlData {
  @_ dutyOrganization: string = '';
  @_ dutyPosition: string = '';
  @_ dutyCountry: string = '';
  @_ dutyDuration: string = '';
  @_ secretAdmitted: NullableString = new NullableString();
  @_ secretAware: NullableString = new NullableString();
  @_ contracts: NullableString = new NullableString();
  @_ trips: NullableString = new NullableString();
}

export class FPMailingInfo {
  @_ active: boolean = false;
  @_ addressRus: string = '';
  @_ addressLat: string = '';
}

export class NotificationOfRefusalFormFields {
  @_ part: string = '';
  @_ article: string = '';
  @_ reasonFZ: string = '';
  @_ reasonAR: string = '';
}

export class Applicant {
  @_ gender: Gender = new Gender();
  @_ personIdentity: PersonIdentity = new PersonIdentity();
  @_ nameChanges: FeatureArray<NameChange> = new FeatureArray<NameChange>(NameChange, []);
  @_ lastNameLat: string = '';
  @_ firstNameLat: string = '';
  @_ birthPlaceFull: string = '';
  @_ birthPlaceRus: string = '';
  @_ birthPlaceLat: string = '';
  @_ secondNationality: NullableString = new NullableString();
  @_ permAddress: PersonAddress = new PersonAddress();
  @_ tempAddress: PersonAddress = new PersonAddress();
  @_ activeFPassport: boolean = true;
  @_ activeFPassports: ActiveFPassportsInfo = new ActiveFPassportsInfo();
  @_ jobRecords: FeatureArray<JobRecord> = new FeatureArray<JobRecord>(JobRecord, []);
  @_ jobManDate: MaskedDate = new MaskedDate();
  @_ jobManName: string = '';
  @_ hrManager: HRManager = new HRManager();
  @_ identityDocument: ApplicantIdentityDocument = new ApplicantIdentityDocument();
  @_ chipPhoto: ImageBlob = new ImageBlob();
  @_ printPhoto: ImageBlob = new ImageBlob();
  @_ signature: ImageBlob = new ImageBlob();
  @_ fingerprints: FingerprintsBlob = new FingerprintsBlob();
}
Features.defaultValue(Applicant, x => x.lastNameLat, Features.transliterate(x => x.personIdentity.fullName.lastName));
Features.defaultValue(Applicant, x => x.firstNameLat, Features.transliterate(x => x.personIdentity.fullName.firstName));
Features.maskedDateOptions(Applicant, x => x.jobManDate, true, false, false, null);

export class Representative {
  @_ gender: Gender = new Gender();
  @_ personIdentity: PersonIdentity = new PersonIdentity();
  @_ birthPlace: string = '';
  @_ address: RepresentativeAddress = new RepresentativeAddress();
  @_ nameChanges: FeatureArray<NameChangeForRep> = new FeatureArray<NameChangeForRep>(NameChangeForRep, []);
  @_ identityDocument: RepresentativeIdentityDocument = new RepresentativeIdentityDocument();
}

// mobile application, web site
export class MobileOptions {
  @_ department: string = '';
  @_ departmentCountry: string = '';
  @_ departmentCity: string = '';
  @_ departmentUsesPassportMail: boolean = false;
  @_ departmentUsesInternetApps: boolean = false;
  @_ sendAppViaInternet: boolean = false;
  @_ sendAppViaInternetWithFIO: boolean = false;
}

// tablet application
export class AmbulanceOptions {
  // department code (9 digit)
  @_ departmentLong: string = '';
}

// desktop application
export class StationaryOptions {
  // department code (9 digit)
  @_ departmentLong: string = '';
  // department code (9 digit) from Ministry of Defence
  @_ departmentMinDefLong: string = '';
  @_ receptionCity: string = '';
  @_ receptionCountry: string = '';
  @_ receptionCode: string = '';
}

export class RequestFeatures {
  @_ applicant: Applicant = new Applicant();
  @_ representative: Representative = new Representative();
  @_ goal: RequestGoal = new RequestGoal();
  @_ residenceCountry: string = '';
  @_ desiredForeignPassportType: ForeignPassportType = ForeignPassportType.Common;
  @_ personalizedForeignPassport: PersonalizedFPassportInfo = new PersonalizedFPassportInfo();
  @_ cause: RequestCause = new RequestCause();
  @_ secretAccess: NullableString = new NullableString();
  @_ departureRestrictions: NullableString = new NullableString();
  @_ militaryService: NullableString = new NullableString();
  @_ conviction: NullableString = new NullableString();
  @_ legalObligations: NullableString = new NullableString();
  @_ mailing: FPMailingInfo = new FPMailingInfo();
  @_ notificationOfRefusalFormFields: NotificationOfRefusalFormFields = new NotificationOfRefusalFormFields();
  @_ citizenshipCertificate: CitizenshipCertificate = new CitizenshipCertificate();
  @_ adultSpecialConditions: AdultSpecialConditions = AdultSpecialConditions.None;
  @_ teenagerSpecialConditions: TeenagerSpecialConditions = TeenagerSpecialConditions.None;
  @_ presidentEdict: PresidentEdict = new PresidentEdict();
  @_ citizenshipDecision: CitizenshipDecision = new CitizenshipDecision();
  @_ soldierPermission: SoldierPermission = new SoldierPermission();
  @_ dipServiceControlData: DipServiceControlData = new DipServiceControlData();
  @_ fingerprintsRequired: FingerprintsRequired = new FingerprintsRequired();
  @_ fingerprintsPresent: boolean = false;
  @_ receptionUser: string = '';
  @_ receptionUserPosition: string = '';
  @_ receptionDate: MaskedDate = new MaskedDate();
  @_ applicantDate: MaskedDate = new MaskedDate();
  @_ inputMedia: InputMedia = new InputMedia();
  @_ mobileOptions: MobileOptions = new MobileOptions();
  @_ ambulanceOptions: AmbulanceOptions = new AmbulanceOptions();
  @_ stationaryOptions: StationaryOptions = new StationaryOptions();
  @_ versionSite: string = '';
  @_ versionAmbulance: string = '';
  @_ instanceAmbulance: string = '';
  @_ versionClient: string = '';
  @_ versionServer: string = '';
  @_ inputMode: InputMode = new InputMode();
  @_ inputWorkPlace: string = '';
  @_ sourceRequestUID: string = '';
  @_ cloneUIDs: string[] = [];

  isAdult(): boolean {
    return Features.ageBetween(this.receptionDate, false, this.applicant.personIdentity.birthDate, false, 18, null, true);
  }

  isTeenager(): boolean {
    return Features.ageBetween(this.receptionDate, false, this.applicant.personIdentity.birthDate, false, 14, 18, false);
  }

  isFingerprintCapable(): boolean {
    return Features.ageBetween(this.receptionDate, false, this.applicant.personIdentity.birthDate, false, 12, null, true);
  }

  isDesiredPassportTypeDipService(): boolean {
    return this.desiredForeignPassportType === ForeignPassportType.Service || this.desiredForeignPassportType === ForeignPassportType.Diplomatic;
  }

  desiredForeignPassportTypesList(): ForeignPassportType[] {
    if (this.canDepartmentIssueDiplomaticPassport()) {
      return [ForeignPassportType.Common, ForeignPassportType.Service, ForeignPassportType.Diplomatic];
    }
    return [ForeignPassportType.Common];
  }

  desiredForeignPassportTypesDefault(): ForeignPassportType | null {
    return null;
  }

  canDepartmentIssueDiplomaticPassport(): boolean {
    // коды подразделений КД, АП и УК
    return (this.stationaryOptions &&
      (this.stationaryOptions.departmentLong === '200007701' ||
        this.stationaryOptions.departmentLong === '200007794' ||
        this.stationaryOptions.departmentLong === '200007795'));
  }

  isHomelandDepartment(): boolean {
    // режимы АП (ФСО), УК, ПСФ, КД
    return (this.stationaryOptions &&
      (this.stationaryOptions.departmentLong === '200007794' ||
        this.stationaryOptions.departmentLong === '200007795' ||
        this.stationaryOptions.departmentLong === '200003901' ||
        this.stationaryOptions.departmentLong === '200007701'));
  }

  asString(): string {
    return this.applicant.personIdentity.fullName.asString();
  }

  get asString_(): string {
    return this.asString();
  }

  asShortString(): string {
    return this.applicant.personIdentity.fullName.asShortString();
  }

  get FPassportDisplayInfo(): string | undefined {
    const passportDisplayString: (p: ActiveFPassportInfo | undefined) => string = p => {
      if (!p || !p.series && !p.number) {
        return '';
      }
      const lostAndCanceledSubString = `${p.mustBeCanceled ? 'А' : ''}`;
      return `${p.series}${p.number}${lostAndCanceledSubString ? `(${lostAndCanceledSubString})` : ''}`;
    }

    const firstPassport = passportDisplayString(this.applicant?.activeFPassports?.p1);
    if (!firstPassport) {
      return '';
    }

    const secondPassport = passportDisplayString(this.applicant?.activeFPassports?.p2);

    return `${firstPassport}${!!secondPassport ? ', ' : ''}${secondPassport} `;
  }

  copyPermAddressToTempAddress() {
    this.applicant.tempAddress.index = this.applicant.permAddress.index;
    this.applicant.tempAddress.country = this.applicant.permAddress.country;
    this.applicant.tempAddress.admDivision.value = this.applicant.permAddress.admDivision.value;
    this.applicant.tempAddress.settlement = this.applicant.permAddress.settlement;
    this.applicant.tempAddress.street.value = this.applicant.permAddress.street.value;
    this.applicant.tempAddress.building.value = this.applicant.permAddress.building.value;
    this.applicant.tempAddress.housing.value = this.applicant.permAddress.housing.value;
    this.applicant.tempAddress.structure.value = this.applicant.permAddress.structure.value;
    this.applicant.tempAddress.flat.value = this.applicant.permAddress.flat.value;
    this.applicant.tempAddress.phone = this.applicant.permAddress.phone;
  }

  setReceptionOfficerPosition(data: string) {
    this.receptionUserPosition = data;
  }

  formatPermAddress(): string {
    return this.formatAddress(this.applicant.permAddress);
  }

  formatTempAddress(): string {
    return this.formatAddress(this.applicant.tempAddress);
  }

  formatAddress(address: PersonAddress): string {
    const parts: string[] = [];
    if (!!address.index) { parts.push(address.index); }
    if (!!address.country) { parts.push(address.country); }
    if (!!address.admDivision.value) { parts.push(address.admDivision.value); }
    if (!!address.settlement) { parts.push(address.settlement); }
    if (!!address.street.value) { parts.push('ул. ' + address.street.value); }
    if (!!address.building.value) { parts.push('д. ' + address.building.value); }
    if (!!address.housing.value) { parts.push('корп. ' + address.housing.value); }
    if (!!address.structure.value) { parts.push('стр. ' + address.structure.value); }
    if (!!address.flat.value) { parts.push('кв. ' + address.flat.value); }
    return parts.join(', ').toUpperCase();
  }

  formatUnemployment(): string {
    return this.applicant.gender.val() !== '1'
      ? FeaturesI18N.getString('field.jobRecords.unemployed-male')
      : FeaturesI18N.getString('field.jobRecords.unemployed-female');
  }

  onBeforeSave(dashSign: () => void) {
    this.applicant.personIdentity.fullName.firstName = Utils.trimNameField(this.applicant.personIdentity.fullName.firstName);
    this.applicant.personIdentity.fullName.lastName = Utils.trimNameField(this.applicant.personIdentity.fullName.lastName);
    if (this.applicant.personIdentity.fullName.middleName.value) {
      this.applicant.personIdentity.fullName.middleName.value = Utils.trimNameField(this.applicant.personIdentity.fullName.middleName.value);
    }
    for (const x of this.applicant.nameChanges) {
      x.firstName = Utils.trimNameField(x.firstName);
      x.lastName = Utils.trimNameField(x.lastName);
      if (x.middleName.value) {
        x.middleName.value = Utils.trimNameField(x.middleName.value);
      }
    }
    this.applicant.lastNameLat = Utils.trimNameField(this.applicant.lastNameLat);
    this.applicant.firstNameLat = Utils.trimNameField(this.applicant.firstNameLat);
    this.applicant.birthPlaceFull = Utils.trimBirthPlaceField(this.applicant.birthPlaceFull);
    this.applicant.birthPlaceLat = Utils.trimBirthPlaceField(this.applicant.birthPlaceLat);
    this.applicant.birthPlaceRus = Utils.trimBirthPlaceField(this.applicant.birthPlaceRus);

    if (this.representative) {
      this.representative.personIdentity.fullName.firstName = Utils.trimNameField(this.representative.personIdentity.fullName.firstName);
      this.representative.personIdentity.fullName.lastName = Utils.trimNameField(this.representative.personIdentity.fullName.lastName);

      const representativeMiddleName = this.representative.personIdentity.fullName.middleName.value;
      if (representativeMiddleName) {
        this.representative.personIdentity.fullName.middleName.value = Utils.trimNameField(representativeMiddleName);
      };
    }
  }
}

Features.documentField(RequestFeatures, DocumentField.Photo, x => x.applicant.chipPhoto);
Features.documentField(RequestFeatures, DocumentField.PhotoHash, x => x.applicant.chipPhoto.hash);
Features.documentField(RequestFeatures, DocumentField.PassportType, 'P');
Features.documentField(RequestFeatures, DocumentField.PassportSubType, x => x.desiredForeignPassportType);
Features.documentField(RequestFeatures, DocumentField.Country, 'RUS');
Features.documentField(RequestFeatures, DocumentField.LastName, x => x.applicant.lastNameLat);
Features.documentField(RequestFeatures, DocumentField.FirstName, x => x.applicant.firstNameLat);
Features.documentField(RequestFeatures, DocumentField.SerialNumber, x => x.personalizedForeignPassport.number);
Features.documentField(RequestFeatures, DocumentField.Nationality, 'RUS');
Features.documentField(RequestFeatures, DocumentField.BirthDate, x => x.applicant.personIdentity.birthDate);
Features.documentField(RequestFeatures, DocumentField.ExpiryDate, x => x.personalizedForeignPassport.expireDate);
Features.documentField(RequestFeatures, DocumentField.Gender, x => x.applicant.gender);

Features.keyboardLanguage(RequestFeatures, x => x.applicant.birthPlaceLat, 'en');
Features.keyboardLanguage(RequestFeatures, x => x.applicant.firstNameLat, 'en');
Features.keyboardLanguage(RequestFeatures, x => x.applicant.lastNameLat, 'en');
Features.keyboardLanguage(RequestFeatures, x => x.mailing.addressLat, 'en');

export class UserFeatures {
  @_ name: string = '';
  @_ lastName: string = '';
  @_ middleName: string = '';
  @_ email: string = '';
  @_ jobPosition: string = '';
  @_ isRcod: boolean = false;
  @_ departmentCode: string = '';

  get asString(): string {
    let result = '';
    if (this.lastName) {
      result += this.lastName.trim();
    }
    if (this.name) {
      result += (result ? ' ' : '') + this.name.trim();
    }
    if (this.middleName) {
      result += (result ? ' ' : '') + this.middleName.trim();
    }
    if (result && this.jobPosition) {
      result += ', ' + this.jobPosition.trim();
    }
    return result;
  }
}
Features.visibleField(UserFeatures, x => x.departmentCode, x => x.isRcod);

Features.enumOptions(RequestFeatures, x => x.desiredForeignPassportType, x => x.desiredForeignPassportType, (x: RequestFeatures) => x.desiredForeignPassportTypesList(), (x: RequestFeatures) => x.desiredForeignPassportTypesDefault());
